/**
 * The editor's viewport camera: ephemeral view state (A-16).
 *
 * Continuous navigation (fly, look, orbit, pan, dolly) writes here directly
 * through `navigate`, as in Unreal, where viewport camera moves aren't
 * transactions. Discrete, reproducible camera changes are commands:
 * `camera.frame` (the F key, the tour), `camera.setView` (snap to Top,
 * Front, …), `camera.setProjection` (perspective or orthographic, independent
 * of direction) and `camera.setPose` (an exact pose; nothing in the UI issues
 * it since the URL dropped `cam=`).
 */
import { parsePayload } from "../../commands/payload";
import type { CommandDefinition } from "../../commands/types";
import type { ChangeScheduler } from "../../core/ChangeScheduler";
import { aabbFromPoints } from "../../core/math/aabb";
import type { Aabb } from "../../core/math/aabb";
import { mat4GetTranslation } from "../../core/math/mat4";
import type { Vec3 } from "../../core/math/vec3";
import { VersionedStore } from "../../core/store";
import type { ReadableStore } from "../../core/store";
import type { CameraDesc } from "../../render/proxies";
import type { EntityId } from "../../world/entity";
import type { TransformSystem } from "../../world/TransformSystem";
import type { WorldReader, WorldSnapshot } from "../../world/World";
import type { SelectionState } from "../selection/Selection";
import {
  describeView,
  frameBounds,
  projectPoint,
  snapToAxis,
  viewFromCamera,
  withProjection,
} from "./cameraMath";
import type { AxisView, Projection, ScreenPoint, ViewCamera } from "./cameraMath";

const DEFAULT_CAMERA: ViewCamera = {
  position: [0, 3, 10],
  yaw: 0,
  pitch: -12,
  fov: 40,
  near: 0.05,
  far: 400,
  focusDistance: 10,
  projection: "perspective",
  orthoHeight: 10,
};

/** The axis views the View menu offers, in Unreal's order. */
export const AXIS_VIEWS: readonly AxisView[] = ["top", "bottom", "left", "right", "front", "back"];
export const PROJECTIONS: readonly Projection[] = ["perspective", "orthographic"];

/** Framing a point entity (no geometry) treats it as a small box around its origin. */
const POINT_FRAME_EXTENT = 0.5;

export class ViewportCamera implements ReadableStore<ViewCamera> {
  private readonly store: VersionedStore<ViewCamera>;
  private width = 1;
  private height = 1;

  constructor(scheduler: ChangeScheduler) {
    this.store = new VersionedStore(scheduler, DEFAULT_CAMERA);
  }

  readonly subscribe = (listener: () => void) => this.store.subscribe(listener);
  readonly getSnapshot = (): ViewCamera => this.store.getSnapshot();

  /** Applies a navigation transition (see cameraMath). Not a command, by design. */
  navigate(transition: (camera: ViewCamera) => ViewCamera): void {
    this.store.publish(transition(this.store.getSnapshot()));
  }

  /** The viewport's CSS-pixel size, for projection and the render aspect ratio. */
  setViewportSize(width: number, height: number): void {
    this.width = Math.max(1, width);
    this.height = Math.max(1, height);
    this.store.publish({ ...this.store.getSnapshot() });
  }

  get viewportSize(): { readonly width: number; readonly height: number } {
    return { width: this.width, height: this.height };
  }

  /** World → viewport pixels for DOM overlays (labels, selection bracket). */
  project(point: Vec3): ScreenPoint {
    return projectPoint(this.store.getSnapshot(), point, this.width, this.height);
  }

  /** What the render backend needs this frame. */
  describe(): CameraDesc {
    return describeView(this.store.getSnapshot());
  }

  /** Turns to an axis view around the focus point, keeping the projection. */
  setView(axis: AxisView): void {
    this.navigate((camera) => snapToAxis(camera, axis));
  }

  /** Switches perspective/orthographic, keeping the direction and what's in view. */
  setProjection(projection: Projection): void {
    this.navigate((camera) => withProjection(camera, projection));
  }

  /**
   * Back to the view the level opened with: its startup camera's, or the
   * editor's default view for a level without one.
   */
  resetToStartup(world: WorldReader, transforms: TransformSystem): void {
    const startup = startupCameraOf(world.getSnapshot());
    if (startup && this.viewFromCamera(startup, world, transforms)) return;
    this.store.publish(DEFAULT_CAMERA);
  }

  /**
   * Takes a camera entity's view (a level's startup camera, Align View to
   * Camera), just ahead of it (`viewFromCamera`). False if it isn't a camera.
   */
  viewFromCamera(id: EntityId, world: WorldReader, transforms: TransformSystem): boolean {
    const camera = world.component(id, "Camera");
    if (!camera) return false;

    const view = this.store.getSnapshot();
    this.store.publish(viewFromCamera(view, transforms.worldMatrix(id), camera.fov as number));
    return true;
  }
}

export interface CameraCommandDeps {
  readonly camera: ViewportCamera;
  readonly world: WorldReader;
  readonly transforms: TransformSystem;
  readonly selection: ReadableStore<SelectionState>;
  /** World-space bounds of rendered geometry under these entities (from the RenderBridge). */
  readonly boundsOf: (ids: readonly EntityId[]) => Aabb | null;
  /** Piloting, which an axis view ends first (it isn't a camera's view); none by default. */
  readonly pilot?: { stop(): void };
}

export function cameraCommands(deps: CameraCommandDeps): CommandDefinition[] {
  const frame: CommandDefinition<{ ids: readonly EntityId[] }, undefined> = {
    type: "camera.frame",
    undoable: false,
    validate: (raw) => {
      const shape = parsePayload(raw, { ids: "string[]?" });
      if (!shape.ok) return shape;
      const ids = (shape.value.ids ?? deps.selection.getSnapshot().ids) as readonly EntityId[];
      const missing = ids.find((id) => !deps.world.get(id));
      if (missing) return { ok: false, error: `No entity '${missing}'` };
      return { ok: true, value: { ids } };
    },
    do: ({ ids }) => {
      const bounds = framingBounds(ids, deps);
      if (bounds) deps.camera.navigate((camera) => frameBounds(camera, bounds));
      return undefined;
    },
    undo: () => undefined,
    describe: ({ ids }) =>
      `Frame ${ids.length === 1 && ids[0] ? (deps.world.get(ids[0])?.name ?? ids[0]) : `${ids.length} entities`}`,
  };

  const setPose: CommandDefinition<{ position: Vec3; yaw: number; pitch: number }, undefined> = {
    type: "camera.setPose",
    undoable: false,
    validate: (raw) => {
      const shape = parsePayload(raw, { position: "unknown", yaw: "number", pitch: "number" });
      if (!shape.ok) return shape;
      if (!isVec3(shape.value.position)) return { ok: false, error: "position must be [x, y, z]" };
      return {
        ok: true,
        value: { position: shape.value.position, yaw: shape.value.yaw, pitch: shape.value.pitch },
      };
    },
    do: (pose) => {
      deps.camera.navigate((camera) => ({ ...camera, ...pose }));
      return undefined;
    },
    undo: () => undefined,
    describe: () => "Set camera",
  };

  /** Takes a camera's view, as Unity's Align View to Selected does; the viewport stays free. */
  const alignTo: CommandDefinition<{ entity: EntityId }, undefined> = {
    type: "camera.alignTo",
    undoable: false,
    validate: (raw) => {
      const shape = parsePayload(raw, { entity: "string" });
      if (!shape.ok) return shape;
      const record = deps.world.get(shape.value.entity as EntityId);
      if (!record) return { ok: false, error: `No entity '${shape.value.entity}'` };
      if (!record.components.has("Camera")) {
        return { ok: false, error: `${record.name} isn't a camera` };
      }
      return { ok: true, value: { entity: record.id } };
    },
    do: ({ entity }) => {
      // Aligning is a look, not a flight: it ends piloting another camera.
      deps.pilot?.stop();
      deps.camera.viewFromCamera(entity, deps.world, deps.transforms);
      return undefined;
    },
    undo: () => undefined,
    describe: ({ entity }) => `Align view to ${deps.world.get(entity)?.name ?? entity}`,
  };

  /** Reset view: back to where the level opened, for a visitor who's lost their way (a phone's orbit under the map). */
  const reset: CommandDefinition<Record<string, never>, undefined> = {
    type: "camera.reset",
    undoable: false,
    validate: () => ({ ok: true, value: {} }),
    do: () => {
      deps.pilot?.stop();
      deps.camera.resetToStartup(deps.world, deps.transforms);
      return undefined;
    },
    undo: () => undefined,
    describe: () => "Reset view",
  };

  const setView: CommandDefinition<{ view: AxisView }, undefined> = {
    type: "camera.setView",
    undoable: false,
    validate: (raw) => {
      const shape = parsePayload(raw, { view: "string" });
      if (!shape.ok) return shape;
      const view = AXIS_VIEWS.find((axis) => axis === shape.value.view);
      return view
        ? { ok: true, value: { view } }
        : { ok: false, error: `No view '${shape.value.view}'` };
    },
    do: ({ view }) => {
      deps.pilot?.stop();
      deps.camera.setView(view);
      return undefined;
    },
    undo: () => undefined,
    describe: ({ view }) => `View ${view}`,
  };

  const setProjection: CommandDefinition<{ projection: Projection }, undefined> = {
    type: "camera.setProjection",
    undoable: false,
    validate: (raw) => {
      const shape = parsePayload(raw, { projection: "string" });
      if (!shape.ok) return shape;
      const projection = PROJECTIONS.find((kind) => kind === shape.value.projection);
      return projection
        ? { ok: true, value: { projection } }
        : { ok: false, error: `No projection '${shape.value.projection}'` };
    },
    do: ({ projection }) => {
      deps.camera.setProjection(projection);
      return undefined;
    },
    undo: () => undefined,
    describe: ({ projection }) => `Projection ${projection}`,
  };

  return [frame, setPose, alignTo, reset, setView, setProjection] as CommandDefinition[];
}

/** Rendered bounds if there are any; otherwise a small box around the entities' origins. */
function framingBounds(ids: readonly EntityId[], deps: CameraCommandDeps): Aabb | null {
  if (ids.length === 0) return null;
  const rendered = deps.boundsOf(ids);
  if (rendered) return rendered;

  const e = POINT_FRAME_EXTENT;
  const corners = ids.flatMap((id) => {
    const [x, y, z] = mat4GetTranslation(deps.transforms.worldMatrix(id));
    return [
      [x - e, y - e, z - e],
      [x + e, y + e, z + e],
    ] as Vec3[];
  });
  return aabbFromPoints(corners);
}

function isVec3(value: unknown): value is Vec3 {
  return (
    Array.isArray(value) &&
    value.length === 3 &&
    value.every((v) => typeof v === "number" && Number.isFinite(v))
  );
}

/** The level's startup camera (its WorldSettings' `startupCamera`), if it names one. */
export function startupCameraOf(world: WorldSnapshot): EntityId | null {
  for (const root of world.roots) {
    const startup = world.entities.get(root)?.components.get("WorldSettings")?.startupCamera;
    if (typeof startup === "string") return startup as EntityId;
  }
  return null;
}
